import type {
    BulkResult,
    JobItem,
    JobSortColumn,
    JobSortState,
} from "@mrflark/core/hooks";
import {
    formatJobDuration,
    formatRelativeTime,
    getJobStatusBadgeVariant,
} from "@mrflark/core/utils";
import {
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    X,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    useEffect,
    useState,
} from "preact/hooks";
import {
    Badge,
    Button,
    Card,
    Checkbox,
    ConfirmModal,
    Input,
    MultiSelectDropdown,
    Select,
    SortableTableHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../mod.tsx";
import type {
    MultiSelectOption,
} from "../mod.tsx";
import type {
    TimePeriodOption,
} from "./types.ts";
import {
    DEFAULT_STATUS_OPTIONS,
    DEFAULT_TIME_PERIODS,
    PAGE_SIZE_OPTIONS,
    STREAM_INTERVAL_OPTIONS,
    getJobTypeLabel,
} from "./types.ts";

type BulkAction = "cancel" | "retry" | "delete" | "reschedule";

type JobHistoryTableProps = {
    jobs: JobItem[];
    totalJobs: number;
    page: number;
    pageSize: number;
    typeFilters: string[];
    statusFilters: string[];
    timePeriod: number;
    startTime: string;
    endTime: string;
    streamIntervalMs: number;
    jobTypes: string[];
    jobTypeLabels?: Record<string, string>;
    statusOptions?: string[];
    timePeriods?: TimePeriodOption[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onTypeFiltersChange: (filters: string[]) => void;
    onStatusFiltersChange: (filters: string[]) => void;
    onTimePeriodChange: (minutes: number) => void;
    onStartTimeChange: (time: string) => void;
    onEndTimeChange: (time: string) => void;
    onStreamIntervalChange: (ms: number) => void;
    onJobClick: (job: JobItem) => void;
    sort?: JobSortState | null;
    onSortChange?: (sort: JobSortState | null) => void;
    headerSlot?: ComponentChildren;
    title?: string;
    onRefresh?: () => void;
    refreshing?: boolean;
    onBulkCancel?: (ids: number[]) => Promise<BulkResult>;
    onBulkRetry?: (ids: number[]) => Promise<BulkResult>;
    onBulkDelete?: (ids: number[]) => Promise<BulkResult>;
    onBulkReschedule?: (
        ids: number[],
        scheduledFor: string,
    ) => Promise<BulkResult>;
};

const CONFIRM_MESSAGES: Record<
    BulkAction,
    { title: string; message: (count: number) => string; confirmText: string; }
> = {
    cancel: {
        title: "Cancel Jobs",
        message: (count) =>
            `Cancel ${count} selected job(s)? Non-cancellable jobs will be skipped.`,
        confirmText: "Cancel Jobs",
    },
    retry: {
        title: "Retry Jobs",
        message: (count) =>
            `Retry ${count} selected job(s)? Only failed jobs will be retried.`,
        confirmText: "Retry Jobs",
    },
    delete: {
        title: "Delete Jobs",
        message: (count) =>
            `Delete ${count} selected job(s)? Processing jobs will be skipped. This cannot be undone.`,
        confirmText: "Delete Jobs",
    },
    reschedule: {
        title: "Reschedule Jobs",
        message: (count) =>
            `Reschedule ${count} selected job(s)? Only pending jobs will be rescheduled.`,
        confirmText: "Reschedule",
    },
};

export const JobHistoryTable = ({
    jobs,
    totalJobs,
    page,
    pageSize,
    typeFilters,
    statusFilters,
    timePeriod,
    startTime,
    endTime,
    streamIntervalMs,
    jobTypes,
    jobTypeLabels,
    statusOptions = DEFAULT_STATUS_OPTIONS,
    timePeriods = DEFAULT_TIME_PERIODS,
    onPageChange,
    onPageSizeChange,
    onTypeFiltersChange,
    onStatusFiltersChange,
    onTimePeriodChange,
    onStartTimeChange,
    onEndTimeChange,
    onStreamIntervalChange,
    onJobClick,
    sort = null,
    onSortChange,
    headerSlot,
    title = "Job Queue",
    onRefresh,
    refreshing = false,
    onBulkCancel,
    onBulkRetry,
    onBulkDelete,
    onBulkReschedule,
}: JobHistoryTableProps): ComponentChildren => {
    const totalPages = Math.ceil(totalJobs / pageSize);
    const isCustomDateActive = timePeriod === 0 &&
        (startTime !== "" || endTime !== "");
    const hasBulkActions =
        !!(onBulkCancel || onBulkRetry || onBulkDelete || onBulkReschedule);

    const typeOptions: MultiSelectOption[] = jobTypes.map((jobType) => ({
        value: jobType,
        label: getJobTypeLabel(jobType, jobTypeLabels),
    }));

    const statusSelectOptions: MultiSelectOption[] = statusOptions.map((
        status,
    ) => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1),
    }));

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [rescheduleTime, setRescheduleTime] = useState("");

    useEffect(() => {
        setSelectedIds(new Set());
    }, [timePeriod, startTime, endTime, typeFilters, statusFilters, page]);

    const toggleJob = (jobId: number): void => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) {
                next.delete(jobId);
            } else {
                next.add(jobId);
            }
            return next;
        });
    };

    const toggleAll = (): void => {
        if (selectedIds.size === jobs.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(jobs.map((job) => job.id)));
        }
    };

    const clearSelection = (): void => {
        setSelectedIds(new Set());
    };

    const isAllSelected = jobs.length > 0 && selectedIds.size === jobs.length;
    const isSomeSelected = selectedIds.size > 0 &&
        selectedIds.size < jobs.length;
    const selectedJobs = jobs.filter((job) => selectedIds.has(job.id));

    const canCancel = selectedJobs.some((job) =>
        job.status === "pending" || job.status === "processing"
    );
    const canRetry = selectedJobs.some((job) => job.status === "failed");
    const canDelete = selectedJobs.some((job) => job.status !== "processing");
    const canReschedule = selectedJobs.some((job) => job.status === "pending");

    const executeBulkAction = async (): Promise<void> => {
        if (!confirmAction) return;
        const ids = [...selectedIds];
        setBulkLoading(true);
        try {
            switch (confirmAction) {
                case "cancel":
                    await onBulkCancel?.(ids);
                    break;
                case "retry":
                    await onBulkRetry?.(ids);
                    break;
                case "delete":
                    await onBulkDelete?.(ids);
                    break;
                case "reschedule":
                    if (rescheduleTime) {
                        await onBulkReschedule?.(
                            ids,
                            new Date(rescheduleTime).toISOString(),
                        );
                    }
                    break;
            }
            clearSelection();
        } finally {
            setBulkLoading(false);
            setConfirmAction(null);
            setRescheduleTime("");
        }
    };

    const columnCount = hasBulkActions ? 9 : 8;

    return (
        <>
            <Card>
                <div className="flex flex-col gap-2 px-4 pt-4 pb-2">
                    <div className="flex items-center gap-3">
                        <h3 className="text-sm font-medium text-text-primary whitespace-nowrap">
                            {title}
                        </h3>
                        <div className="flex items-center gap-1 ml-auto">
                            {timePeriods.map((period) => (
                                <button
                                    type="button"
                                    key={period.minutes}
                                    onClick={() =>
                                        onTimePeriodChange(period.minutes)}
                                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                        timePeriod === period.minutes &&
                                            !isCustomDateActive
                                            ? "bg-accent text-white"
                                            : "bg-bg-secondary text-text-secondary hover:bg-bg-tertiary"
                                    }`}
                                >
                                    {period.label}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    if (!isCustomDateActive) {
                                        onStartTimeChange("");
                                    }
                                }}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                    isCustomDateActive
                                        ? "bg-accent text-white"
                                        : "bg-bg-secondary text-text-secondary hover:bg-bg-tertiary"
                                }`}
                            >
                                Custom
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <label className="flex items-center gap-1 text-xs text-text-muted">
                            Type
                            <MultiSelectDropdown
                                options={typeOptions}
                                selected={typeFilters}
                                onChange={onTypeFiltersChange}
                                className="w-32"
                            />
                        </label>
                        <label className="flex items-center gap-1 text-xs text-text-muted">
                            Status
                            <MultiSelectDropdown
                                options={statusSelectOptions}
                                selected={statusFilters}
                                onChange={onStatusFiltersChange}
                                className="w-28"
                            />
                        </label>
                        {isCustomDateActive && (
                            <>
                                <label className="flex items-center gap-1 text-xs text-text-muted">
                                    From
                                    <Input
                                        type="datetime-local"
                                        value={startTime}
                                        onChange={(event) =>
                                            onStartTimeChange(
                                                (event
                                                    .target as HTMLInputElement)
                                                    .value,
                                            )}
                                        className="w-44 !py-1 !text-xs"
                                    />
                                </label>
                                <label className="flex items-center gap-1 text-xs text-text-muted">
                                    To
                                    <Input
                                        type="datetime-local"
                                        value={endTime}
                                        onChange={(event) =>
                                            onEndTimeChange(
                                                (event
                                                    .target as HTMLInputElement)
                                                    .value,
                                            )}
                                        className="w-44 !py-1 !text-xs"
                                    />
                                </label>
                            </>
                        )}
                        <label className="flex items-center gap-1 text-xs text-text-muted">
                            Rows
                            <Select
                                value={String(pageSize)}
                                onChange={(event) =>
                                    onPageSizeChange(
                                        Number(
                                            (event.target as HTMLSelectElement)
                                                .value,
                                        ),
                                    )}
                                className="w-16"
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </Select>
                        </label>
                        <label className="flex items-center gap-1 text-xs text-text-muted">
                            Refresh
                            <Select
                                value={String(streamIntervalMs)}
                                onChange={(event) =>
                                    onStreamIntervalChange(
                                        Number(
                                            (event.target as HTMLSelectElement)
                                                .value,
                                        ),
                                    )}
                                className="w-16"
                            >
                                {STREAM_INTERVAL_OPTIONS.map(({
                                    label,
                                    ms,
                                }) => (
                                    <option key={ms} value={ms}>{label}</option>
                                ))}
                            </Select>
                        </label>
                        {onRefresh && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onRefresh}
                                title="Refresh"
                            >
                                <RefreshCw
                                    size={14}
                                    className={refreshing ? "animate-spin" : ""}
                                />
                            </Button>
                        )}
                        {headerSlot}
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            {hasBulkActions && (
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={isSomeSelected}
                                        onChange={toggleAll}
                                    />
                                </TableHead>
                            )}
                            {onSortChange
                                ? (
                                    <>
                                        <SortableTableHeader<JobSortColumn>
                                            column="id"
                                            label="ID"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="asc"
                                            className="w-16"
                                        />
                                        <SortableTableHeader<JobSortColumn>
                                            column="jobType"
                                            label="Type"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="asc"
                                        />
                                        <SortableTableHeader<JobSortColumn>
                                            column="status"
                                            label="Status"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="asc"
                                        />
                                        <TableHead className="w-24">
                                            Progress
                                        </TableHead>
                                        <SortableTableHeader<JobSortColumn>
                                            column="createdAt"
                                            label="Created"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="desc"
                                        />
                                        <SortableTableHeader<JobSortColumn>
                                            column="scheduledFor"
                                            label="Scheduled"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="desc"
                                        />
                                        <SortableTableHeader<JobSortColumn>
                                            column="duration"
                                            label="Duration"
                                            currentSort={sort}
                                            onSortChange={onSortChange}
                                            initialDirection="desc"
                                        />
                                        <TableHead>Error</TableHead>
                                    </>
                                )
                                : (
                                    <>
                                        <TableHead className="w-16">
                                            ID
                                        </TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="w-24">
                                            Progress
                                        </TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Scheduled</TableHead>
                                        <TableHead>Duration</TableHead>
                                        <TableHead>Error</TableHead>
                                    </>
                                )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {jobs.length === 0
                            ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columnCount}
                                        className="text-center text-text-secondary py-8"
                                    >
                                        No jobs found
                                    </TableCell>
                                </TableRow>
                            )
                            : (
                                jobs.map((job) => (
                                    <TableRow
                                        key={job.id}
                                        className={`cursor-pointer hover:bg-bg-secondary ${
                                            selectedIds.has(job.id)
                                                ? "bg-accent/10"
                                                : ""
                                        }`}
                                        onClick={() => onJobClick(job)}
                                    >
                                        {hasBulkActions && (
                                            <TableCell
                                                onClick={(event) =>
                                                    event.stopPropagation()}
                                            >
                                                <Checkbox
                                                    checked={selectedIds.has(
                                                        job.id,
                                                    )}
                                                    onChange={() =>
                                                        toggleJob(job.id)}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell className="text-text-secondary text-xs">
                                            {job.id}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            {getJobTypeLabel(
                                                job.jobType,
                                                jobTypeLabels,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={getJobStatusBadgeVariant(
                                                    job.status,
                                                )}
                                            >
                                                {job.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            {job.status === "processing" &&
                                                    job.progress != null
                                                ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="flex-1 h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-accent rounded-full transition-all duration-500"
                                                                style={{
                                                                    width:
                                                                        `${job.progress}%`,
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-text-secondary tabular-nums w-8 text-right">
                                                            {job.progress}%
                                                        </span>
                                                    </div>
                                                )
                                                : (
                                                    <span className="text-text-secondary">
                                                        &mdash;
                                                    </span>
                                                )}
                                        </TableCell>
                                        <TableCell className="text-text-secondary text-xs">
                                            {formatRelativeTime(job.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-text-secondary text-xs">
                                            {formatRelativeTime(
                                                job.scheduledFor,
                                            )}
                                        </TableCell>
                                        <TableCell className="text-text-secondary text-xs">
                                            {formatJobDuration(
                                                job.startedAt,
                                                job.completedAt,
                                            )}
                                        </TableCell>
                                        <TableCell className="text-red-500 text-xs truncate max-w-[200px]">
                                            {job.errorMessage
                                                ? job.errorMessage.slice(0, 60)
                                                : "\u2014"}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                    </TableBody>
                </Table>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-bg-tertiary">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Prev
                        </Button>
                        <span className="text-sm text-text-secondary">
                            Page {page} of {totalPages}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                        >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                )}
            </Card>

            {hasBulkActions && selectedIds.size > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-bg-secondary border-t border-border p-4 z-40">
                    <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <span className="text-text-primary font-medium">
                                {selectedIds.size}{" "}
                                job{selectedIds.size !== 1 ? "s" : ""} selected
                            </span>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="text-text-secondary hover:text-text-primary transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            {onBulkCancel && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={!canCancel}
                                    onClick={() => setConfirmAction("cancel")}
                                >
                                    Cancel
                                </Button>
                            )}
                            {onBulkRetry && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={!canRetry}
                                    onClick={() => setConfirmAction("retry")}
                                >
                                    Retry
                                </Button>
                            )}
                            {onBulkReschedule && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={!canReschedule}
                                    onClick={() =>
                                        setConfirmAction("reschedule")}
                                >
                                    Reschedule
                                </Button>
                            )}
                            {onBulkDelete && (
                                <Button
                                    variant="error"
                                    size="sm"
                                    disabled={!canDelete}
                                    onClick={() => setConfirmAction("delete")}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {confirmAction && confirmAction !== "reschedule" && (
                <ConfirmModal
                    isOpen
                    onClose={() => setConfirmAction(null)}
                    onConfirm={executeBulkAction}
                    title={CONFIRM_MESSAGES[confirmAction].title}
                    message={CONFIRM_MESSAGES[confirmAction].message(
                        selectedIds.size,
                    )}
                    confirmText={CONFIRM_MESSAGES[confirmAction].confirmText}
                    variant={confirmAction === "delete" ? "danger" : "warning"}
                    isLoading={bulkLoading}
                />
            )}

            {confirmAction === "reschedule" && (
                <ConfirmModal
                    isOpen
                    onClose={() => {
                        setConfirmAction(null);
                        setRescheduleTime("");
                    }}
                    onConfirm={executeBulkAction}
                    title={CONFIRM_MESSAGES.reschedule.title}
                    message={CONFIRM_MESSAGES.reschedule.message(
                        selectedIds.size,
                    )}
                    confirmText={CONFIRM_MESSAGES.reschedule.confirmText}
                    variant="warning"
                    isLoading={bulkLoading || !rescheduleTime}
                >
                    <div className="mt-3 mb-2">
                        <label className="flex flex-col gap-1 text-sm text-text-secondary">
                            Schedule for
                            <Input
                                type="datetime-local"
                                value={rescheduleTime}
                                onChange={(event) =>
                                    setRescheduleTime(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    )}
                                className="w-full"
                            />
                        </label>
                    </div>
                </ConfirmModal>
            )}
        </>
    );
};
